/**
 * MODULE: _shared/frontend-shell/src/design-system/components/Navigation/Sidebar.tsx
 * PURPOSE: Reusable sidebar navigation component using Flowbite
 * 
 * DEPENDENCIES:
 *   - flowbite-react: Sidebar component, CustomFlowbiteTheme
 *   - react-router-dom: Link, useLocation
 *   - React: ReactNode, useState, useEffect
 * 
 * PUBLIC API:
 *   - Sidebar: Main component
 *   - SidebarProps: Component props interface
 *   - SidebarItem: Navigation item interface
 * 
 * SIDE EFFECTS:
 *   - React rendering
 *   - localStorage (collapsed state persistence)
 * 
 * AGENT SAFETY: 🟢 SAFE - Presentational component
 * FILE SIZE: 240 / 300 lines
 * 
 * Agent Notes:
 * - Uses Flowbite Sidebar component with separate imports (SidebarItems, SidebarItemGroup, SidebarItem)
 * - Supports role-based navigation items
 * - Responsive with mobile drawer
 * - Persists collapsed state in localStorage
 * - Supports customHeader prop for tenant branding (added 2026-01-26)
 * - Fixed Flowbite component imports: Replaced compound syntax (FlowbiteSidebar.Logo, FlowbiteSidebar.CTA) 
 *   with custom div elements styled to match theme (2026-01-26)
 * - 2026-04-10: Flowbite `inner` theme includes `app-shell-disappearing-scroll` (root CHANGELOG [2.2.354]).
 * - 2026-01-26: Fixed Flowbite component import errors
 * 
 * See: /README.md | /ARCHITECTURE.md | /_shared/frontend-shell/README.md
 */

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Sidebar as FlowbiteSidebar,
  SidebarItems,
  SidebarItemGroup,
  SidebarItem as FlowbiteSidebarItem,
} from 'flowbite-react';
import type { CustomFlowbiteTheme } from 'flowbite-react/types';

interface SidebarItem {
  path: string;
  label: string;
  /** Heroicons / custom SVG; narrowed at render for Flowbite SidebarItem. */
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
}

interface SidebarProps {
  userRole: 'super_admin' | 'tenant_admin' | 'user';
  activePath: string;
  items: SidebarItem[];
  user?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  customHeader?: ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'brazn:sidebar:collapsed';

/** Flowbite types `SidebarItem` for div/anchor; react-router `Link` uses `to`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge Flowbite + RR Link props
const SidebarItem = FlowbiteSidebarItem as any;

const customTheme: CustomFlowbiteTheme['sidebar'] = {
  root: {
    base: 'h-full',
    collapsed: {
      on: 'w-16',
      off: 'w-64',
    },
    inner:
      'app-shell-disappearing-scroll h-full overflow-y-auto overflow-x-hidden bg-gray-50 px-3 py-4 dark:bg-gray-800',
  },
  collapse: {
    button: 'group flex w-full items-center rounded-lg p-2 text-base font-normal text-gray-900 transition duration-75 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700',
    icon: {
      base: 'h-6 w-6 flex-shrink-0 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white',
      open: {
        off: '',
        on: 'text-gray-900',
      },
    },
    label: {
      base: 'ml-3 flex-1 whitespace-nowrap text-left',
      title: '',
      icon: {
        base: 'h-6 w-6',
        open: { off: '', on: '' },
      },
    },
    list: 'space-y-2 py-2',
  },
  cta: {
    base: 'mt-6 rounded-lg p-4 bg-gray-100 dark:bg-gray-700',
    color: {
      blue: 'bg-blue-50 dark:bg-blue-900',
      dark: 'bg-dark-50 dark:bg-dark-900',
      failure: 'bg-red-50 dark:bg-red-900',
      gray: 'bg-gray-50 dark:bg-gray-700',
      green: 'bg-green-50 dark:bg-green-900',
      light: 'bg-light-50 dark:bg-light-900',
      red: 'bg-red-50 dark:bg-red-900',
      purple: 'bg-purple-50 dark:bg-purple-900',
      success: 'bg-green-50 dark:bg-green-900',
      yellow: 'bg-yellow-50 dark:bg-yellow-900',
      warning: 'bg-yellow-50 dark:bg-yellow-900',
    },
  },
  item: {
    base: 'flex items-center justify-center rounded-lg p-2 text-base font-normal text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700',
    active: 'bg-gray-100 dark:bg-gray-700',
    collapsed: {
      insideCollapse: 'group w-full pl-8 transition duration-75',
      noIcon: 'font-bold',
    },
    content: {
      base: 'px-3 flex-1 whitespace-nowrap',
    },
    icon: {
      base: 'h-6 w-6 flex-shrink-0 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white',
      active: 'text-gray-700 dark:text-gray-100',
    },
    label: '',
    listItem: '',
  },
  items: { base: '' },
  itemGroup: {
    base:
      'mt-4 space-y-2 border-t border-gray-200 pt-4 first:mt-0 first:border-t-0 first:pt-0 dark:border-gray-700',
  },
  logo: {
    base: 'mb-5 flex items-center pl-2.5',
    collapsed: {
      on: 'hidden',
      off: 'self-center whitespace-nowrap text-xl font-semibold dark:text-white',
    },
    img: 'mr-3 h-6 sm:h-7',
  },
};

export function Sidebar({
  userRole: _userRole,
  activePath: _activePath,
  items,
  user,
  onLogout,
  collapsed: controlledCollapsed,
  onToggleCollapse,
  customHeader,
}: SidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });

  const location = useLocation();
  const collapsed = controlledCollapsed ?? internalCollapsed;

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const handleToggle = () => {
    const newCollapsed = !collapsed;
    if (onToggleCollapse) {
      onToggleCollapse(newCollapsed);
    } else {
      setInternalCollapsed(newCollapsed);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <FlowbiteSidebar
      theme={customTheme}
      collapsed={collapsed}
      className="fixed left-0 top-0 z-40 h-screen transition-transform"
    >
      {customHeader ? (
        <div className="mb-5 px-2.5">
          {customHeader}
        </div>
      ) : (
        <div className="mb-5 flex items-center pl-2.5">
          <img src="/brazn-logo.png" alt="Brazn logo" className="mr-3 h-6 sm:h-7 rounded-md object-contain" />
          {!collapsed && <span className="self-center whitespace-nowrap text-xl font-semibold dark:text-white">Brazn</span>}
        </div>
      )}

      <SidebarItems>
        <SidebarItemGroup>
          {items.map((item) => {
            const IconComponent = item.icon;
            return (
              <SidebarItem
                key={item.path}
                as={Link}
                to={item.path}
                active={isActive(item.path)}
                icon={
                  IconComponent as React.FC<React.ComponentProps<'svg'>>
                }
              >
                {!collapsed && item.label}
                {item.badge && !collapsed && (
                  <span className="ml-auto rounded-full bg-primary-600 px-2 py-0.5 text-xs text-white">
                    {item.badge}
                  </span>
                )}
              </SidebarItem>
            );
          })}
        </SidebarItemGroup>
      </SidebarItems>

      {user && (
        <div className="mt-6 rounded-lg p-4 bg-gray-100 dark:bg-gray-700">
          <div className="mb-3 flex items-center">
            <div className="mr-3 h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name || 'User'}
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                <span className="text-gray-600 font-medium">
                  {user.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {user.name || 'User'}
                </p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {user.email || ''}
                </p>
              </div>
            )}
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="w-full rounded-lg bg-primary-700 px-3 py-2 text-center text-sm font-medium text-white hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
            >
              {collapsed ? '🚪' : 'Sign Out'}
            </button>
          )}
        </div>
      )}

      <button
        onClick={handleToggle}
        className="absolute bottom-4 right-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? '→' : '←'}
      </button>
    </FlowbiteSidebar>
  );
}
